import { createRouter, createWebHashHistory } from 'vue-router'
import Inscription from '@/views/Inscription.vue'
import PhoneConfirmation from '@/views/PhoneConfirmation.vue'
import Authentication from '@/views/Authentication.vue'
import EditInformation from '@/views/EditInformation.vue'
import Dashboard from '@/views/Dashboard.vue'
import EditInformationOtpConfirmation from '@/views/EditInformationOtpConfirmation.vue'
import AdminAuthentication from '@/views/admin/AdminAuthentication.vue'
import AdminDashboard from '@/views/admin/AdminDashboard.vue'
import { useSubscriberStore } from '@/stores/subscriber'
import { useAdminStore } from '@/stores/admin'
import { getCurrentUser } from '@/api/subscription'
import AdminForgotPassword from '@/views/admin/AdminForgotPassword.vue'
import AdminRecoverPassword from '@/views/admin/AdminRecoverPassword.vue'
import SuccessRegistration from '@/views/SuccessRegistration.vue'
import AdminEdit from '@/views/admin/AdminEdit.vue'
import AdminList from '@/views/admin/AdminList.vue'
import AdminCreate from '@/views/admin/AdminCreate.vue'

const isAdmin = (userType) => {
  return userType === 'admin' || userType === 's-admin';
}

const routes = [
  { path: '/', name: 'home', component: Inscription },
  { path: '/inscription', name: 'inscription', component: Inscription },
  { path: '/confirmation-telephone', name: 'PhoneConfirmation', component: PhoneConfirmation },
  { path: '/inscription-reussie', name: 'SuccessfullRegistration', component: SuccessRegistration },
  {
    path: '/edit',
    name: 'edit-info',
    component: EditInformation,
    meta: { requiresAuth: true, role: 'subscriber' }
  },
  { path: '/login', name: 'login', component: Authentication },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
    meta: { requiresAuth: true, role: 'subscriber' }
  },
  {
    path: '/verify-edit',
    name: 'verifyEdit',
    component: EditInformationOtpConfirmation,
    meta: { requiresAuth: true, role: 'subscriber' }
  },
  {
    path: '/admin', name: 'admin', component: AdminAuthentication,
    meta: { requiresAuth: false }
  },
  {
    path: '/admin/edit', name: 'admin-edit', component: AdminEdit,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/dashboard',
    name: 'adminDashboard',
    component: AdminDashboard,
    meta: { requiresAuth: true, role: 'admin' }
  },
  {
    path: '/admin/forgot-password',
    name: 'forgotPassword',
    component: AdminForgotPassword
  },
  {
    path: '/admin/recover-password',
    name: 'recoverPassword',
    component: AdminRecoverPassword
  },
  {
    path: '/admin/list',
    name: 'adminsList',
    component: AdminList,
    meta: { requiresAuth: true, role: 's-admin' }
  },
  {
    path: '/admin/create',
    name: 'adminsCreate',
    component: AdminCreate,
    meta: { requiresAuth: true, role: 's-admin' }
  }
];

const router = createRouter({
  history: createWebHashHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  const token = localStorage.getItem('auth_token');
  const adminStore = useAdminStore();
  const subscriberStore = useSubscriberStore();

  let currentUserType = adminStore.type || subscriberStore.type;
  let currentUserActive = null;

  if (token && !currentUserType) {
    try {
      const response = await getCurrentUser();
      if (response.data.success) {
        const currentUser = response.data.user;
        currentUserType = currentUser.type;
        currentUserActive = currentUser.active;

        if (isAdmin(currentUserType)) {
          // Check if admin is active before setting admin data
          if (currentUserActive === 0) {
            localStorage.removeItem('auth_token');
            return next('/admin');
          }
          adminStore.setAdmin(response.data);
        } else if (currentUserType === 'subscriber') {
          subscriberStore.setAuthData({
            token,
            ...currentUser,
            ...response.data.subscription,
          });
        }
      } else {
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      localStorage.removeItem('auth_token');
      console.error('Error fetching user:', error);
    }
  } else if (token && currentUserType) {
    // Use stored active status
    currentUserActive = isAdmin(currentUserType) ? adminStore.active : null;
  }

  // Check if admin is inactive and trying to access admin routes
  if (isAdmin(currentUserType) && currentUserActive === 0) {
    localStorage.removeItem('auth_token');
    adminStore.clearAdmin();
    return next('/admin');
  }

  if (to.meta.requiresAuth) {
    if (!token || !currentUserType) {
      return next((to.meta.role === 'admin' || to.meta.role === 's-admin') ? '/admin' : '/login');
    }

    // Check role compatibility
    if (to.meta.role) {
      let hasAccess = false;

      if (to.meta.role === 'admin' && isAdmin(currentUserType)) {
        hasAccess = true;
      } else if (to.meta.role === 's-admin' && currentUserType === 's-admin') {
        hasAccess = true;
      } else if (to.meta.role === 'subscriber' && currentUserType === 'subscriber') {
        hasAccess = true;
      }

      if (!hasAccess) {
        return next(isAdmin(currentUserType) ? '/admin/dashboard' : '/edit');
      }
    }
  }

  // Redirect authenticated users away from login pages
  if (to.name === 'login' && currentUserType === 'subscriber') return next('/edit');
  if (to.name === 'admin' && currentUserType === 'subscriber') return next('/edit');

  if (to.name === 'admin' && isAdmin(currentUserType)) return next('/admin/dashboard');
  if (to.name === 'login' && isAdmin(currentUserType)) return next('/admin/dashboard');

  next();
});

export default router